// Who made this, where else they live, and whose icons these are.
//
// **One component, because it appears on more than one page** and a copyright
// line that says different things in different places is a copyright line
// nobody can rely on. The year is computed rather than typed: a notice that
// says 2026 in 2027 is the sort of small wrongness that makes a reader doubt
// the rest of the page.

import { Link, styled, Text } from "./kit";

/** The entity behind the game. Named once here and read everywhere it appears. */
export const OWNER = "Gnarled Helix LLC";
export const OWNER_URL = "https://gnarledhelix.com";

/**
 * The icon pack's authors, in the order `assets/LICENSES.md` records them.
 *
 * **Attribution is an obligation, so it ships where the icons are seen.** The
 * pack's own licence asks for "Icons made by {author}", and game-icons is many
 * contributors — so naming the site alone would not discharge it. One line,
 * because a licence is satisfied by the mention and not by its length.
 */
const ICON_AUTHORS = "Lorc, Delapouite, Guard13007, Sbed and Skoll";
const ICONS_URL = "https://game-icons.net";
const ICONS_LICENCE = "https://creativecommons.org/licenses/by/3.0/";

const Bar = styled("footer")(({ theme }) => ({
  marginTop: theme.kit.space.xl,
  paddingTop: theme.kit.space.md,
  borderTop: `2px solid ${theme.kit.color.line}`,
  display: "flex",
  alignItems: "center",
  gap: theme.kit.space.md,
  flexWrap: "wrap"
}));

/**
 * The copyright line, and whatever else belongs at the bottom of a page.
 *
 * `children` rather than a fixed set of links: the menu wants the privacy
 * notice beside this and a document page does not, and the alternative is a
 * flag per caller for something the caller can simply pass.
 */
export function Footer({ children }: { children?: React.ReactNode }): React.ReactElement {
  return (
    <Bar data-testid="footer">
      <Text tone="dim" size={12}>
        © {new Date().getFullYear()}{" "}
        <Link
          href={OWNER_URL}
          // **`noreferrer` as well as `noopener`.** The first is what stops the
          // opened page reaching back through `window.opener`; the second stops
          // it being told where the visitor came from, which is a thing this
          // page has no business leaking on somebody's behalf.
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-owner"
        >
          {OWNER}
        </Link>
      </Text>
      <Text tone="dim" size={12} data-testid="footer-icons">
        Icons by {ICON_AUTHORS} (
        <Link href={ICONS_URL} target="_blank" rel="noopener noreferrer">
          game-icons.net
        </Link>
        ,{" "}
        <Link href={ICONS_LICENCE} target="_blank" rel="noopener noreferrer">
          CC BY 3.0
        </Link>
        )
      </Text>
      {children}
    </Bar>
  );
}
